// Hitter truth — process vs results, with the team as its own baseline.
//
// Due list: expected AVG per hitter from the TEAM's own batted-ball conversion
// rates. We estimate how often this team's line drives / grounders / flies
// become hits (ridge-regularized least squares over the roster — 3 unknowns,
// zero league inputs), then score each hitter's mix against his actual AVG.
// Sample floors keep it honest at youth scale.
//
// Also: traffic conversion (RBI vs runners stranded) and two-strike battle rate.
//
// Pure + framework-free. Consumes buildSeasonStats batting buckets.

export interface DueFlag {
  playerId: string;
  player: any;
  actualAvg: number;
  expectedAvg: number;
  /** actual − expected; negative = due, positive = riding luck */
  delta: number;
  verdict: 'due' | 'luck';
  /** best contact-quality note for the card copy */
  ldPct: number | null;
  hhb: number | null;
}

export interface TeamConversionRates {
  ld: number; gb: number; fb: number;
  /** total balls in play the estimate is built on */
  bip: number;
}

export interface HitterTruthResult {
  rates: TeamConversionRates | null;
  flags: DueFlag[];
  traffic: Array<{ playerId: string; player: any; conversion: number; rbi: number; lob: number }>;
  battle: Array<{ playerId: string; player: any; battlePct: number; pa: number }>;
}

const MIN_TEAM_BIP = 60;
const MIN_PLAYERS = 6;
const MIN_PA = 15;
const FLAG_DELTA = 0.045;
const MIN_TRAFFIC_OPPS = 8;

interface HitterRow { id: string; player: any; bip: number; hits: number; mix: [number, number, number]; pa: number; ab: number; avg: number | null; ldPct: number | null; hhb: number | null }

function rows(statsMap: Record<string, any>): HitterRow[] {
  return Object.entries(statsMap).map(([id, s]: [string, any]) => {
    const b = s.batting || {};
    const bip = Math.max(0, (b.ab || 0) - (b.so || 0)); // HR ≈ 0 at youth level
    const ld = b.ldPct, gb = b.gbPct, fb = b.fbPct;
    const hasMix = ld != null && gb != null && fb != null && (ld + gb + fb) > 0;
    const sum = hasMix ? ld + gb + fb : 1;
    return {
      id, player: s.player, bip, hits: b.h || 0, pa: b.pa || 0, ab: b.ab || 0,
      avg: b.avg ?? null, ldPct: ld ?? null, hhb: b.hhb ?? null,
      mix: hasMix ? [ld / sum, gb / sum, fb / sum] as [number, number, number] : [0, 0, 0],
    };
  });
}

/**
 * Ridge-regularized 3×3 normal equations: hits_i ≈ bip_i · (mix_i · rates),
 * shrunk toward PHYSICAL contact priors (line drives fall for hits far more
 * often than fly balls — a modeling prior, not a league table). On a youth
 * roster (~10 noisy points) an unanchored fit can invert reality (GB 14%,
 * FB 53%); the prior keeps rates sane while the team's own data still moves
 * them. Solve (XᵀX + λI) r = Xᵀy + λ·prior.
 */
const RATE_PRIOR = { ld: 0.62, gb: 0.26, fb: 0.16 };

function estimateRates(hs: HitterRow[]): TeamConversionRates | null {
  const usable = hs.filter(h => h.bip > 0 && (h.mix[0] + h.mix[1] + h.mix[2]) > 0);
  const totalBip = usable.reduce((a, h) => a + h.bip, 0);
  if (usable.length < MIN_PLAYERS || totalBip < MIN_TEAM_BIP) return null;

  // λ scales with sample size: strong pull at 60 BIP, data dominates by ~500+.
  const lambda = totalBip * 1.5;
  const prior = [RATE_PRIOR.ld, RATE_PRIOR.gb, RATE_PRIOR.fb];
  const A = [[lambda, 0, 0], [0, lambda, 0], [0, 0, lambda]];
  const v = prior.map(p => p * lambda);
  usable.forEach(h => {
    const x = h.mix.map(m => m * h.bip);
    for (let i = 0; i < 3; i++) {
      v[i] += x[i] * h.hits;
      for (let j = 0; j < 3; j++) A[i][j] += x[i] * x[j];
    }
  });
  // Gaussian elimination
  const M = A.map((row, i) => [...row, v[i]]);
  for (let c = 0; c < 3; c++) {
    let piv = c;
    for (let r = c + 1; r < 3; r++) if (Math.abs(M[r][c]) > Math.abs(M[piv][c])) piv = r;
    if (Math.abs(M[piv][c]) < 1e-9) return null;
    [M[c], M[piv]] = [M[piv], M[c]];
    for (let r = 0; r < 3; r++) {
      if (r === c) continue;
      const f = M[r][c] / M[c][c];
      for (let k = c; k < 4; k++) M[r][k] -= f * M[c][k];
    }
  }
  const clamp = (x: number) => Math.max(0.08, Math.min(0.75, x));
  return {
    ld: clamp(M[0][3] / M[0][0]),
    gb: clamp(M[1][3] / M[1][1]),
    fb: clamp(M[2][3] / M[2][2]),
    bip: totalBip,
  };
}

export function computeHitterTruth(statsMap: Record<string, any>): HitterTruthResult {
  const hs = rows(statsMap);
  const rates = estimateRates(hs);

  const flags: DueFlag[] = [];
  if (rates) {
    hs.forEach(h => {
      if (h.pa < MIN_PA || h.ab <= 0 || h.avg == null) return;
      if ((h.mix[0] + h.mix[1] + h.mix[2]) === 0) return;
      // Leave-one-out: score each hitter against rates fit on his TEAMMATES,
      // so a hot streak can't drag the baseline toward itself and hide luck.
      const loo = estimateRates(hs.filter(o => o.id !== h.id)) ?? rates;
      const xHits = h.bip * (h.mix[0] * loo.ld + h.mix[1] * loo.gb + h.mix[2] * loo.fb);
      const expectedAvg = xHits / h.ab;
      const delta = h.avg - expectedAvg;
      if (Math.abs(delta) < FLAG_DELTA) return;
      flags.push({
        playerId: h.id, player: h.player, actualAvg: h.avg, expectedAvg, delta,
        verdict: delta < 0 ? 'due' : 'luck', ldPct: h.ldPct, hhb: h.hhb,
      });
    });
    flags.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  }

  const traffic = Object.entries(statsMap)
    .map(([id, s]: [string, any]) => {
      const b = s.batting || {};
      if (b.rbi == null || b.lob == null) return null;
      const opps = b.rbi + b.lob;
      if (opps < MIN_TRAFFIC_OPPS) return null;
      return { playerId: id, player: s.player, conversion: b.rbi / opps, rbi: b.rbi, lob: b.lob };
    })
    .filter((t): t is NonNullable<typeof t> => t != null)
    .sort((a, b) => b.conversion - a.conversion);

  const battle = Object.entries(statsMap)
    .map(([id, s]: [string, any]) => {
      const b = s.batting || {};
      if (b.battlePct == null || (b.pa || 0) < MIN_PA) return null;
      return { playerId: id, player: s.player, battlePct: b.battlePct, pa: b.pa };
    })
    .filter((t): t is NonNullable<typeof t> => t != null)
    .sort((a, b) => b.battlePct - a.battlePct);

  return { rates, flags, traffic, battle };
}
